import React, { useState, useEffect, useRef } from 'react';
import ImageDesigner from '../ImageDesigner';
import ShopAppearancePreview from '../ShopAppearancePreview';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Store as StoreIcon,
  Title as TitleIcon,
  Image as ImageIcon,
  ViewCarousel as BannerIcon,
  ErrorOutline as ErrorIcon,
  Visibility as PreviewIcon,
  OpenInNew as OpenInNewIcon,
  ContentCopy as ContentCopyIcon,
  Link as LinkIcon,
  ViewModule as LayoutIcon,
  Palette as ThemeIcon,
  TextFields as TypographyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AppearanceService from '../../services/AppearanceService';
import NotificationService from '../../services/NotificationService';
import { preparePhotoForApi } from '../../utils/mediaPrepare';
import { useAuth } from '../auth/AuthProvider';
import {
  DEFAULT_PRESENTATION_SETTINGS,
  mergePresentationSettings,
} from '../../constants/presentationSettings';
import './AppearanceDesigner.css';

const APP_URL = process.env.REACT_APP_PUBLIC_URL || process.env.REACT_APP_APP_URL || 'http://app.local.dev:8080';
const LOGO_FORMAT_HINT =
  'Carré (1:1) — affiché en 128 px sur la page boutique. Taille idéale : 512 × 512 px.';
const BANNER_FORMAT_HINT =
  'Paysage (3:1) — bannière pleine largeur (hauteur 192–256 px). Taille idéale : 1200 × 400 px.';

const slugify = (text) =>
  String(text || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/œ/g, 'oe')
    .replace(/æ/g, 'ae')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const FIELD_SECTIONS = {
  name: 'section-texts',
  title: 'section-texts',
  slug: 'section-texts',
  logo: 'section-logo',
  banner: 'section-banner',
  products: 'section-product-display',
};

const AppearanceDesigner = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const sectionRefs = useRef({});

  const [shopData, setShopData] = useState({
    id: null,
    name: '',
    title: '',
    slug: '',
    logo: null,
    banner: null,
  });

  const [presentationData, setPresentationData] = useState(DEFAULT_PRESENTATION_SETTINGS);
  const [expandedSections, setExpandedSections] = useState({
    layout: false,
    theme: false,
    typography: false,
    productDisplay: true,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [activeField, setActiveField] = useState('name');
  const [suggestedSlugs, setSuggestedSlugs] = useState([]);
  const [slugError, setSlugError] = useState('');
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [slugPolicy, setSlugPolicy] = useState({
    can_change_slug: true,
    next_change_at: null,
    active_redirect: null,
  });

  const effectiveSlug = slugify(shopData.slug) || user?.shopSlug || '';
  const publicShopUrl = effectiveSlug ? `${APP_URL}/boutiques/${effectiveSlug}` : null;
  const canChangeSlug = slugPolicy?.can_change_slug !== false;

  const formatPolicyDate = (isoDate) => {
    if (!isoDate) return null;
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString('fr-FR');
  };

  const nextChangeLabel = formatPolicyDate(slugPolicy?.next_change_at);

  useEffect(() => {
    loadShopData();
  }, []);

  const scrollToSection = (field) => {
    const sectionId = FIELD_SECTIONS[field];
    const section = sectionRefs.current[sectionId];
    if (section && typeof section.scrollIntoView === 'function') {
      section.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const handleFieldSelect = (field) => {
    setActiveField(field);
    scrollToSection(field);

    if (field === 'products') {
      setExpandedSections((prev) => ({ ...prev, productDisplay: true, layout: true }));
    }
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const loadShopData = async () => {
    setIsLoading(true);
    setAuthError(null);
    try {
      const appearanceData = await AppearanceService.getShopAppearance();

      if (appearanceData) {
        setShopData({
          ...appearanceData,
          slug: appearanceData.slug || '',
        });
        setSlugPolicy(
          appearanceData.slug_policy || {
            can_change_slug: true,
            next_change_at: null,
            active_redirect: null,
          }
        );
        setPresentationData(mergePresentationSettings(appearanceData.presentation));
        setIsEditing(true);
      } else {
        setPresentationData(DEFAULT_PRESENTATION_SETTINGS);
        if (user?.shopSlug) {
          setShopData((prev) => ({ ...prev, slug: user.shopSlug }));
        }
      }
    } catch (error) {
      console.error("Erreur lors du chargement des données de l'apparence:", error);

      if (error.response?.status === 401) {
        setAuthError({
          type: 'auth',
          message: 'Votre session a expiré. Veuillez vous reconnecter.',
        });
      } else if (error.response?.status === 403) {
        setAuthError({
          type: 'forbidden',
          message:
            error.response?.data?.error ||
            "Vous n'avez pas les droits pour accéder à cette ressource.",
        });
      } else {
        NotificationService.error('Erreur lors du chargement des données');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const updateShopData = (updates) => {
    setShopData((prev) => ({ ...prev, ...updates }));
    setHasChanges(true);
    if (Object.prototype.hasOwnProperty.call(updates, 'slug')) {
      setSuggestedSlugs([]);
      setSlugError('');
    }
  };

  const handleCopyPublicUrl = async () => {
    if (!publicShopUrl) return;
    try {
      await navigator.clipboard.writeText(publicShopUrl);
      setCopyFeedback(true);
      window.setTimeout(() => setCopyFeedback(false), 2000);
    } catch (error) {
      NotificationService.error("Impossible de copier l'adresse");
    }
  };

  const handleSuggestionClick = (slug) => {
    updateShopData({ slug });
    setSuggestedSlugs([]);
    setSlugError('');
  };

  const updatePresentation = (section, field, value) => {
    setPresentationData((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
    setHasChanges(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSlugError('');
    setSuggestedSlugs([]);

    try {
      const [logo, banner] = await Promise.all([
        preparePhotoForApi(shopData.logo),
        preparePhotoForApi(shopData.banner),
      ]);

      const appearanceData = {
        name: shopData.name,
        title: shopData.title,
        slug: slugify(shopData.slug) || undefined,
        logo,
        banner,
        presentation: presentationData,
      };

      let response;
      if (isEditing) {
        response = await AppearanceService.updateShopAppearance(appearanceData);
      } else {
        response = await AppearanceService.saveShopAppearance(appearanceData);
        setIsEditing(true);
      }

      if (response) {
        setShopData({
          ...response,
          slug: response.slug || '',
        });
        if (response.slug_policy) {
          setSlugPolicy(response.slug_policy);
        }
        setPresentationData(mergePresentationSettings(response.presentation));
      }

      setHasChanges(false);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);

      if (error.response?.status === 401) {
        setAuthError({
          type: 'auth',
          message: 'Votre session a expiré. Veuillez vous reconnecter.',
        });
      } else if (error.response?.status === 403) {
        setAuthError({
          type: 'forbidden',
          message:
            error.response?.data?.error ||
            "Vous n'avez pas les droits pour modifier cette ressource.",
        });
      } else if (
        error.response?.status === 409 ||
        error.response?.status === 429 ||
        error.response?.data?.code === 'SHOP_SLUG_TAKEN' ||
        error.response?.data?.code === 'SHOP_SLUG_INVALID' ||
        error.response?.data?.code === 'SHOP_SLUG_COOLDOWN'
      ) {
        const payload = error.response?.data || {};
        setSlugError(payload.error || "Cette adresse de boutique n'est pas disponible.");
        setSuggestedSlugs(payload.suggested_slugs || []);
        if (payload.next_change_at) {
          setSlugPolicy((prev) => ({
            ...prev,
            can_change_slug: false,
            next_change_at: payload.next_change_at,
          }));
        }
        setActiveField('slug');
        scrollToSection('slug');
      } else {
        NotificationService.error('Erreur lors de la sauvegarde');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard');
  };

  const SectionToggle = ({ icon: Icon, title, sectionKey }) => (
    <button
      type="button"
      className="appearance-designer__section-toggle"
      onClick={() => toggleSection(sectionKey)}
      aria-expanded={expandedSections[sectionKey]}
    >
      <span className="appearance-designer__section-toggle-label">
        <Icon fontSize="small" />
        {title}
      </span>
      {expandedSections[sectionKey] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
    </button>
  );

  if (isLoading && !hasChanges) {
    return (
      <div className="appearance-designer appearance-designer--loading">
        <div className="appearance-designer__loader">Chargement...</div>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="appearance-designer">
        <div className="appearance-designer__error">
          <ErrorIcon style={{ fontSize: 48, color: '#dc2626', marginBottom: 16 }} />
          <h2>{authError.type === 'auth' ? 'Session expirée' : 'Accès refusé'}</h2>
          <p>{authError.message}</p>
          <div className="appearance-designer__error-actions">
            <button type="button" onClick={() => navigate('/dashboard')}>
              Retour au tableau de bord
            </button>
            {authError.type === 'auth' && (
              <button type="button" className="primary" onClick={() => navigate('/login')}>
                Se reconnecter
              </button>
            )}
            {authError.type === 'forbidden' && (
              <a href="/become-creator" className="primary">
                Devenir créateur
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="appearance-designer">
      <header className="appearance-designer__header">
        <div>
          <h1 className="appearance-designer__title">
            <StoreIcon className="appearance-designer__title-icon" />
            {isEditing ? 'Apparence de la boutique' : 'Configurer votre boutique'}
          </h1>
          <p className="appearance-designer__subtitle">
            Modifiez l&apos;identité visuelle et la présentation — l&apos;aperçu reflète le rendu
            public sur l&apos;app Ritaaa.
          </p>
        </div>
        <div className="appearance-designer__header-actions">
          {publicShopUrl ? (
            <a
              href={publicShopUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="appearance-designer__btn appearance-designer__btn--ghost"
            >
              <OpenInNewIcon fontSize="small" />
              Voir ma boutique
            </a>
          ) : null}
          <button
            type="button"
            className="appearance-designer__btn appearance-designer__btn--ghost"
            onClick={handleCancel}
            disabled={isLoading}
          >
            <CancelIcon fontSize="small" />
            Annuler
          </button>
          <button
            type="submit"
            form="appearance-form"
            className="appearance-designer__btn appearance-designer__btn--primary"
            disabled={isLoading || !hasChanges}
          >
            <SaveIcon fontSize="small" />
            {isLoading ? 'Sauvegarde...' : isEditing ? 'Mettre à jour' : 'Sauvegarder'}
          </button>
        </div>
      </header>

      <div className="appearance-designer__workspace">
        <div className="appearance-designer__preview-column">
          <div className="appearance-designer__preview-label">
            <PreviewIcon fontSize="small" />
            Aperçu en direct
          </div>
          <ShopAppearancePreview
            name={shopData.name}
            title={shopData.title}
            slug={effectiveSlug}
            logo={shopData.logo}
            banner={shopData.banner}
            presentation={presentationData}
            activeField={activeField}
            onFieldSelect={handleFieldSelect}
            onNameChange={(value) => updateShopData({ name: value })}
            onTitleChange={(value) => updateShopData({ title: value })}
            interactive
          />
        </div>

        <aside className="appearance-designer__panel">
          <form id="appearance-form" onSubmit={handleSubmit} className="appearance-designer__form">
            <section
              ref={(el) => {
                sectionRefs.current['section-texts'] = el;
              }}
              className={`appearance-designer__section ${
                activeField === 'name' || activeField === 'title' || activeField === 'slug'
                  ? 'appearance-designer__section--active'
                  : ''
              }`}
            >
              <h2 className="appearance-designer__section-title">
                <TitleIcon fontSize="small" />
                Textes
              </h2>
              <div className="appearance-designer__field">
                <label htmlFor="name">Nom de la boutique</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={shopData.name}
                  onChange={(e) => updateShopData({ name: e.target.value })}
                  onFocus={() => setActiveField('name')}
                  placeholder="Ex. Atelier des Collines"
                  required
                />
              </div>
              <div className="appearance-designer__field">
                <label htmlFor="title">Slogan / accroche</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={shopData.title}
                  onChange={(e) => updateShopData({ title: e.target.value })}
                  onFocus={() => setActiveField('title')}
                  placeholder="Ex. Céramique artisanale faite main"
                  required
                />
              </div>
              <div className="appearance-designer__field">
                <label htmlFor="slug">
                  <LinkIcon fontSize="inherit" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  Adresse publique
                </label>
                <div className="appearance-designer__slug-input">
                  <span className="appearance-designer__slug-prefix">/boutiques/</span>
                  <input
                    type="text"
                    id="slug"
                    name="slug"
                    value={shopData.slug}
                    onChange={(e) => updateShopData({ slug: e.target.value })}
                    onFocus={() => setActiveField('slug')}
                    placeholder="mon-atelier"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    disabled={isLoading || !canChangeSlug}
                    readOnly={!canChangeSlug}
                  />
                </div>
                <p className="appearance-designer__hint">
                  Le nom affiché et l&apos;adresse peuvent différer. Modifier l&apos;adresse change le
                  lien partagé. Limite : 1 modification tous les 30 jours.
                </p>
                {!canChangeSlug && nextChangeLabel ? (
                  <p className="appearance-designer__slug-policy" role="status">
                    Prochaine modification possible le {nextChangeLabel}.
                  </p>
                ) : null}
                {slugPolicy?.active_redirect?.from_slug ? (
                  <p className="appearance-designer__slug-policy" role="status">
                    Ancienne adresse{' '}
                    <code>/boutiques/{slugPolicy.active_redirect.from_slug}</code> redirige de
                    façon permanente.
                  </p>
                ) : null}
                {publicShopUrl ? (
                  <div className="appearance-designer__public-url">
                    <code title={publicShopUrl}>{publicShopUrl}</code>
                    <button
                      type="button"
                      className="appearance-designer__public-url-btn"
                      onClick={handleCopyPublicUrl}
                      disabled={isLoading}
                    >
                      <ContentCopyIcon fontSize="small" />
                      {copyFeedback ? 'Copié' : 'Copier'}
                    </button>
                    <a
                      href={publicShopUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="appearance-designer__public-url-btn"
                    >
                      <OpenInNewIcon fontSize="small" />
                      Ouvrir
                    </a>
                  </div>
                ) : null}
                {slugError ? (
                  <p className="appearance-designer__field-error" role="alert">
                    {slugError}
                  </p>
                ) : null}
                {suggestedSlugs.length > 0 ? (
                  <div className="appearance-designer__slug-suggestions">
                    <p>Adresses disponibles :</p>
                    <div className="appearance-designer__slug-suggestion-list">
                      {suggestedSlugs.map((slug) => (
                        <button
                          key={slug}
                          type="button"
                          className="appearance-designer__slug-suggestion"
                          disabled={isLoading}
                          onClick={() => handleSuggestionClick(slug)}
                        >
                          {slug}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </section>

            <section
              ref={(el) => {
                sectionRefs.current['section-logo'] = el;
              }}
              className={`appearance-designer__section ${
                activeField === 'logo' ? 'appearance-designer__section--active' : ''
              }`}
              onFocus={() => setActiveField('logo')}
            >
              <h2 className="appearance-designer__section-title">
                <ImageIcon fontSize="small" />
                Logo
              </h2>
              <p className="appearance-designer__hint">{LOGO_FORMAT_HINT}</p>
              <ImageDesigner
                image={shopData.logo}
                onImageChange={(image) => updateShopData({ logo: image })}
                variant="square"
                formatHint={LOGO_FORMAT_HINT}
              />
            </section>

            <section
              ref={(el) => {
                sectionRefs.current['section-banner'] = el;
              }}
              className={`appearance-designer__section ${
                activeField === 'banner' ? 'appearance-designer__section--active' : ''
              }`}
              onFocus={() => setActiveField('banner')}
            >
              <h2 className="appearance-designer__section-title">
                <BannerIcon fontSize="small" />
                Bannière
              </h2>
              <p className="appearance-designer__hint">{BANNER_FORMAT_HINT}</p>
              <ImageDesigner
                image={shopData.banner}
                onImageChange={(image) => updateShopData({ banner: image })}
                variant="banner"
                formatHint={BANNER_FORMAT_HINT}
              />
            </section>

            <section className="appearance-designer__section appearance-designer__section--collapsible">
              <SectionToggle icon={LayoutIcon} title="Mise en page" sectionKey="layout" />
              {expandedSections.layout && (
                <div className="appearance-designer__section-body">
                  <div className="appearance-designer__field-row">
                    <div className="appearance-designer__field">
                      <label htmlFor="productsPerRow">Produits par ligne</label>
                      <select
                        id="productsPerRow"
                        value={presentationData.layout.productsPerRow}
                        onChange={(e) =>
                          updatePresentation('layout', 'productsPerRow', parseInt(e.target.value, 10))
                        }
                      >
                        <option value={2}>2 produits</option>
                        <option value={3}>3 produits</option>
                        <option value={4}>4 produits</option>
                        <option value={5}>5 produits</option>
                      </select>
                    </div>
                    <div className="appearance-designer__field">
                      <label htmlFor="categoriesPosition">Position des catégories</label>
                      <select
                        id="categoriesPosition"
                        value={presentationData.layout.categoriesPosition}
                        onChange={(e) =>
                          updatePresentation('layout', 'categoriesPosition', e.target.value)
                        }
                      >
                        <option value="sidebar">Barre latérale</option>
                        <option value="top">En haut</option>
                        <option value="hidden">Masquées</option>
                      </select>
                    </div>
                  </div>
                  <div className="appearance-designer__checkboxes">
                    <label className="appearance-designer__checkbox">
                      <input
                        type="checkbox"
                        checked={presentationData.layout.showCategories}
                        onChange={(e) =>
                          updatePresentation('layout', 'showCategories', e.target.checked)
                        }
                      />
                      Afficher les catégories
                    </label>
                    <label className="appearance-designer__checkbox">
                      <input
                        type="checkbox"
                        checked={presentationData.layout.showFilters}
                        onChange={(e) =>
                          updatePresentation('layout', 'showFilters', e.target.checked)
                        }
                      />
                      Afficher les filtres
                    </label>
                    <label className="appearance-designer__checkbox">
                      <input
                        type="checkbox"
                        checked={presentationData.layout.showSearch}
                        onChange={(e) =>
                          updatePresentation('layout', 'showSearch', e.target.checked)
                        }
                      />
                      Afficher la recherche
                    </label>
                  </div>
                </div>
              )}
            </section>

            <section className="appearance-designer__section appearance-designer__section--collapsible">
              <SectionToggle icon={ThemeIcon} title="Couleurs et thème" sectionKey="theme" />
              {expandedSections.theme && (
                <div className="appearance-designer__section-body">
                  <div className="appearance-designer__color-grid">
                    {[
                      ['primaryColor', 'Couleur principale'],
                      ['secondaryColor', 'Couleur secondaire'],
                      ['backgroundColor', 'Arrière-plan'],
                      ['textColor', 'Couleur du texte'],
                      ['accentColor', "Couleur d'accent"],
                    ].map(([key, label]) => (
                      <div key={key} className="appearance-designer__field appearance-designer__field--color">
                        <label htmlFor={key}>{label}</label>
                        <input
                          type="color"
                          id={key}
                          value={presentationData.theme[key]}
                          onChange={(e) => updatePresentation('theme', key, e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>

            <section className="appearance-designer__section appearance-designer__section--collapsible">
              <SectionToggle icon={TypographyIcon} title="Typographie" sectionKey="typography" />
              {expandedSections.typography && (
                <div className="appearance-designer__section-body">
                  <div className="appearance-designer__field">
                    <label htmlFor="fontFamily">Police de caractères</label>
                    <select
                      id="fontFamily"
                      value={presentationData.typography.fontFamily}
                      onChange={(e) => updatePresentation('typography', 'fontFamily', e.target.value)}
                    >
                      <option value="Inter">Inter</option>
                      <option value="Roboto">Roboto</option>
                      <option value="Open Sans">Open Sans</option>
                      <option value="Lato">Lato</option>
                      <option value="Montserrat">Montserrat</option>
                      <option value="Poppins">Poppins</option>
                    </select>
                  </div>
                  <div className="appearance-designer__field-row">
                    <div className="appearance-designer__field">
                      <label htmlFor="headingSize">Taille des titres</label>
                      <select
                        id="headingSize"
                        value={presentationData.typography.headingSize}
                        onChange={(e) =>
                          updatePresentation('typography', 'headingSize', e.target.value)
                        }
                      >
                        <option value="small">Petit</option>
                        <option value="medium">Moyen</option>
                        <option value="large">Grand</option>
                      </select>
                    </div>
                    <div className="appearance-designer__field">
                      <label htmlFor="bodySize">Taille du texte</label>
                      <select
                        id="bodySize"
                        value={presentationData.typography.bodySize}
                        onChange={(e) => updatePresentation('typography', 'bodySize', e.target.value)}
                      >
                        <option value="small">Petit</option>
                        <option value="normal">Normal</option>
                        <option value="large">Grand</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </section>

            <section
              ref={(el) => {
                sectionRefs.current['section-product-display'] = el;
              }}
              className={`appearance-designer__section appearance-designer__section--collapsible ${
                activeField === 'products' ? 'appearance-designer__section--active' : ''
              }`}
            >
              <SectionToggle icon={LayoutIcon} title="Affichage des produits" sectionKey="productDisplay" />
              {expandedSections.productDisplay && (
                <div className="appearance-designer__section-body">
                  <div className="appearance-designer__field-row">
                    <div className="appearance-designer__field">
                      <label htmlFor="imageAspectRatio">Format des images</label>
                      <select
                        id="imageAspectRatio"
                        value={presentationData.productDisplay.imageAspectRatio}
                        onChange={(e) =>
                          updatePresentation('productDisplay', 'imageAspectRatio', e.target.value)
                        }
                      >
                        <option value="1:1">Carré (1:1)</option>
                        <option value="4:3">Paysage (4:3)</option>
                        <option value="3:4">Portrait (3:4)</option>
                        <option value="16:9">Large (16:9)</option>
                      </select>
                    </div>
                    <div className="appearance-designer__field">
                      <label htmlFor="hoverEffect">Effet au survol</label>
                      <select
                        id="hoverEffect"
                        value={presentationData.productDisplay.hoverEffect}
                        onChange={(e) =>
                          updatePresentation('productDisplay', 'hoverEffect', e.target.value)
                        }
                      >
                        <option value="none">Aucun</option>
                        <option value="zoom">Zoom</option>
                        <option value="shadow">Ombre</option>
                        <option value="lift">Élévation</option>
                      </select>
                    </div>
                  </div>
                  <div className="appearance-designer__checkboxes">
                    <label className="appearance-designer__checkbox">
                      <input
                        type="checkbox"
                        checked={presentationData.productDisplay.showPrice}
                        onChange={(e) =>
                          updatePresentation('productDisplay', 'showPrice', e.target.checked)
                        }
                      />
                      Afficher le prix
                    </label>
                    <label className="appearance-designer__checkbox">
                      <input
                        type="checkbox"
                        checked={presentationData.productDisplay.showDescription}
                        onChange={(e) =>
                          updatePresentation('productDisplay', 'showDescription', e.target.checked)
                        }
                      />
                      Afficher la description
                    </label>
                    <label className="appearance-designer__checkbox">
                      <input
                        type="checkbox"
                        checked={presentationData.productDisplay.showStock}
                        onChange={(e) =>
                          updatePresentation('productDisplay', 'showStock', e.target.checked)
                        }
                      />
                      Afficher le stock
                    </label>
                  </div>
                </div>
              )}
            </section>
          </form>
        </aside>
      </div>
    </div>
  );
};

export default AppearanceDesigner;
